/**
 * Slots in a session.
 *
 * Beeline started as a pair and is now a room, so "the peer" is no longer a thing that exists —
 * there is a set of members, and each of them is one of these slots. The identifiers stay single
 * letters because they ride on every position frame: at eight people sending a fix a second,
 * the difference between `'a'` and a UUID is the difference between a byte and forty.
 *
 * A slot identifies someone *within one session only*. It is not stable across sessions and it
 * is not an account, which is the whole point.
 */

/** Every slot a session can hand out, in the order they are handed out. */
export const SLOTS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

export type ParticipantId = (typeof SLOTS)[number];

/**
 * The hard ceiling on a room, as opposed to the capacity a given session chose.
 *
 * Eight is a design decision, not a technical one: past that the rim of the compass runs out of
 * space for chevrons and a roll call stops being readable at arm's length in the sun.
 */
export const MAX_PARTICIPANTS = SLOTS.length;

export function isParticipantId(value: unknown): value is ParticipantId {
  return typeof value === 'string' && (SLOTS as readonly string[]).includes(value);
}

/** The first slot nobody holds, or null when the room is full. */
export function nextFreeSlot(
  taken: Iterable<ParticipantId>,
  capacity: number = MAX_PARTICIPANTS,
): ParticipantId | null {
  const held = new Set(taken);
  const limit = Math.min(Math.max(1, Math.floor(capacity)), MAX_PARTICIPANTS);

  for (let index = 0; index < limit; index++) {
    const slot = SLOTS[index];
    if (slot && !held.has(slot)) return slot;
  }
  return null;
}

/** Everyone in `all` except `self`. The room's answer to "the peer". */
export function othersThan(all: readonly ParticipantId[], self: ParticipantId): ParticipantId[] {
  return all.filter((slot) => slot !== self);
}

/**
 * Clamp a requested room size.
 *
 * Two is the default because the hero flow is still two people finding each other; a group is a
 * deliberate choice the creator makes, not something they fall into.
 */
export function clampCapacity(requested: unknown, fallback = 2): number {
  const value = typeof requested === 'number' ? requested : Number.parseInt(String(requested), 10);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(Math.max(2, Math.floor(value)), MAX_PARTICIPANTS);
}
